import { useState } from "react";
import { api } from "../api";
import { ErrorState } from "../ui/State";
import type { Evidence, Packet, Result, StructureResult } from "./xero-history-import-types";

export function XeroPayableNetting({
  packet,
  structure,
  evidence,
  reason,
  root,
  busy,
  setBusy,
  stop,
}: {
  packet: Packet;
  structure: StructureResult | undefined;
  evidence: (checksum: string) => Omit<Evidence, "title">;
  reason: string;
  root: string;
  busy: boolean;
  setBusy: (value: boolean) => void;
  stop: { current: boolean };
}) {
  const items = packet.payableNettingMappings ?? [],
    [results, setResults] = useState<Record<string, Result>>({}),
    [page, setPage] = useState(0);
  async function copy() {
    if (!structure) return;
    setBusy(true);
    stop.current = false;
    try {
      for (const m of items) {
        if (stop.current) break;
        if (results[m.adjustment.sourceId]?.canonicalId) continue;
        try {
          const member = (s: { sourceId: string; sourceChecksum: string }) => {
            const event = packet.history.events.find((e) => e.sourceId === s.sourceId),
              date = (event?.lines[0] as { date?: string } | undefined)?.date;
            const periods = structure.periods.filter((p) => date && p.startsOn <= date && p.endsOn >= date);
            if (!event || periods.length !== 1)
              throw Error("Prepare one original full-year period for each source date.");
            return { sourceId: s.sourceId, periodId: periods[0]!.id, evidence: evidence(s.sourceChecksum) };
          };
          const payments = (packet.history.payments ?? []).filter((p) =>
            m.payments.some((s) => s.sourceId === p.sourceId),
          );
          const ids = new Set([
            m.adjustment.sourceId,
            ...m.payments.map((s) => s.sourceId),
            ...payments.flatMap((p) => p.applications.map((a) => a.invoiceSourceId)),
          ]);
          const { checksum, ...witness } = m.bankWitness;
          const result = await api<{ groupId: string }>(root + "/payable-netting", {
            method: "POST",
            body: JSON.stringify({
              history: {
                organisation: packet.history.organisation,
                selector: packet.history.selector,
                buildingId: packet.history.buildingId,
                events: packet.history.events.filter((e) => ids.has(e.sourceId)),
                invoices: packet.history.invoices.filter((i) => ids.has(i.sourceId)),
                payments,
                accountMappings: packet.history.accountMappings ?? [],
              },
              scheduleId: structure.scheduleId,
              bankTransactionId: m.bankTransactionId,
              bankWitness: { ...witness, evidence: evidence(checksum) },
              payments: m.payments.map(member),
              adjustment: { ...member(m.adjustment), costCodeId: m.adjustment.costCodeId },
              reason,
            }),
          });
          setResults((r) => ({ ...r, [m.adjustment.sourceId]: { canonicalId: result.groupId } }));
        } catch (error) {
          setResults((r) => ({ ...r, [m.adjustment.sourceId]: { error } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="Original supplier net bank settlements">
      <h2>Original supplier net bank settlements</h2>
      <p>
        {items.length} original settlement groups · {Object.values(results).filter((r) => r.canonicalId).length}{" "}
        confirmed in this session.
      </p>
      <p>
        Original gross invoice payments link to their recorded reconciliation adjustment. Together they
        explain the actual bank debit.
      </p>
      <button disabled={busy || !structure || reason.trim().length < 5} onClick={() => void copy()}>
        Copy original supplier net bank settlements
      </button>
      <table>
        <thead>
          <tr>
            <th>Original reference</th>
            <th>Invoices</th>
            <th>Import result</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(page * 25, (page + 1) * 25).map((m) => (
            <tr key={m.adjustment.sourceId}>
              <td>{m.reference}</td>
              <td>{m.payments.length}</td>
              <td>
                {results[m.adjustment.sourceId]?.canonicalId ? (
                  "Copied"
                ) : results[m.adjustment.sourceId]?.error ? (
                  <ErrorState error={results[m.adjustment.sourceId]?.error} />
                ) : (
                  "Ready for source checks"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 25 && (
        <nav aria-label="Original supplier settlement pages">
          <button disabled={!page} onClick={() => setPage(page - 1)}>
            Previous
          </button>{" "}
          Page {page + 1} of {Math.ceil(items.length / 25)}{" "}
          <button disabled={(page + 1) * 25 >= items.length} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
